import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { FilterPanelComponent, FilterField } from '../../../shared/components/filter-panel/filter-panel.component';
import { JoinOrganisationRequestComponent } from '../join-organisation-request/join-organisation-request.component';
import { RoleUpgradationRequestComponent } from '../role-upgradation-request/role-upgradation-request.component';

@Component({
  selector: 'app-user-requests-home',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    FilterPanelComponent,
    JoinOrganisationRequestComponent,
    RoleUpgradationRequestComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class UserRequestsHomeComponent implements OnInit {
  @ViewChild('joinOrgRequests') joinOrgRequests?: JoinOrganisationRequestComponent;
  @ViewChild('roleUpgradeRequests') roleUpgradeRequests?: RoleUpgradationRequestComponent;

  activeTabIndex = 0;
  private isInitialLoad = true;

  tabs = [
    {
      key: 'join-org',
      label: 'Organisation Join Requests',
      icon: '<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"></path><path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88"></path>',
      route: '/org-admin/user-requests/join-organisation-request'
    },
    {
      key: 'role-upgrade',
      label: 'Role Upgradation',
      icon: '<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"></path>',
      route: '/org-admin/user-requests/role-upgradation-request'
    }
  ];

  // Filter fields - Only Date Range for join-organisation-request
  filterFields: FilterField[] = [
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'dateRange',
    },
  ];

  // Filter values - DateRange uses startDate and endDate (Date objects)
  dateRange: { startDate: Date | null; endDate: Date | null } | null = null;
  
  // Store filters object to prevent creating new references on every change detection
  private _currentFilters: Record<string, any> = {
    dateRange: null
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Determine active tab based on current route
    const currentPath = this.router.url;
    if (currentPath.includes('role-upgradation-request')) {
      this.activeTabIndex = 1;
    } else {
      this.activeTabIndex = 0;
      // If on base route, redirect to join-organisation-request
      if (currentPath === '/org-admin/user-requests') {
        this.router.navigate(['/org-admin/user-requests/join-organisation-request'], { replaceUrl: true });
      }
    }
    
    // Mark initial load as complete after a delay to prevent filter panel init events from triggering API calls
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 200);
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    // Navigate to the corresponding route
    this.router.navigate([this.tabs[index].route]);
  }

  onExport(): void {
    if (this.activeTabIndex === 0) {
      void this.joinOrgRequests?.onExport();
      return;
    }
    void this.roleUpgradeRequests?.onExport();
  }

  onFilterChange(filters: Record<string, any>): void {
    // Skip filter changes during initial load to prevent duplicate API calls
    if (this.isInitialLoad) {
      return;
    }

    // Update filter values - only dateRange for join-organisation-request
    if (filters['dateRange'] !== undefined) {
      this.dateRange = filters['dateRange'];
    }

    // Emit filter change to child components
    this.emitFilterChange();
  }

  onClearFilters(): void {
    this.dateRange = null;
    this.emitFilterChange();
  }

  private emitFilterChange(): void {
    // Skip during initial load
    if (this.isInitialLoad) {
      return;
    }

    // Update the filters object only when filters actually change
    // This prevents creating new object references on every change detection
    // Convert DateRange format (startDate/endDate) to API format (start/end as ISO strings)
    let apiDateRange: { start: string; end: string } | null = null;
    
    if (this.dateRange && this.dateRange.startDate && this.dateRange.endDate) {
      const toLocalDateStr = (d: Date): string => {
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      };
      const startDate = this.dateRange.startDate instanceof Date
        ? toLocalDateStr(this.dateRange.startDate)
        : String(this.dateRange.startDate);
      const endDate = this.dateRange.endDate instanceof Date
        ? toLocalDateStr(this.dateRange.endDate)
        : String(this.dateRange.endDate);
      
      apiDateRange = {
        start: startDate,
        end: endDate
      };
    }
    
    // Only update if the dateRange actually changed
    const newFilters = {
      dateRange: apiDateRange
    };
    
    // Compare to prevent unnecessary updates
    const currentStr = JSON.stringify(this._currentFilters);
    const newStr = JSON.stringify(newFilters);
    
    if (currentStr !== newStr) {
      this._currentFilters = newFilters;
    }
  }

  get currentFilters(): Record<string, any> {
    // Return the stored filters object instead of creating a new one
    return this._currentFilters;
  }
}
